<template>
  <main class="app-main">
    <div class="app-content-header">
      <div class="container-fluid">
        <div class="dashboard-heading">
          <div class="dashboard-heading__content">
            <span class="dashboard-heading__eyebrow">نظرة شاملة</span>
            <h1>لوحة التحكم</h1>
            <p>مؤشرات أعمالك العقارية في مكان واحد، بوضوح يساعدك على اتخاذ القرار.</p>
          </div>
          <div class="dashboard-heading__mark" aria-hidden="true">
            <img src="/assets/img/brand-mark.svg" alt="">
          </div>
        </div>
      </div>
    </div>

    <div class="app-content">
      <div class="container-fluid">

        <div v-if="mainLoading" class="dash-loading">
          <div class="spinner-border text-primary" role="status"></div>
          <p class="mt-3 text-muted mb-0">جاري تحميل بيانات لوحة التحكم…</p>
        </div>

        <div v-if="!mainLoading">
          <!-- DASHBOARD FILTER BAR -->
          <div class="dash-filter-bar mb-4">
            <div class="dash-filter-bar__left">
              <i class="bi bi-funnel-fill me-2 text-primary"></i>
              <span class="dash-filter-bar__title">تصفية لوحة التحكم</span>
              <span v-if="appliedDateFrom || appliedDateTo || appliedCityId || appliedRegionId" class="dash-filter-active-chips">
                <span v-if="appliedDateFrom" class="dash-chip">من: {{ appliedDateFrom }}</span>
                <span v-if="appliedDateTo" class="dash-chip">إلى: {{ appliedDateTo }}</span>
                <span v-if="appliedRegionId" class="dash-chip">{{ (regionsList.find(r => r.id == appliedRegionId) || {}).name }}</span>
                <span v-if="appliedCityId" class="dash-chip">{{ cityNameById(appliedCityId) }}</span>
              </span>
            </div>
            <div class="dash-filter-bar__right">
              <button
                v-if="appliedDateFrom || appliedDateTo || appliedCityId || appliedRegionId"
                class="dash-filter-btn dash-filter-btn--clear"
                @click="clearFilter"
              ><i class="bi bi-x-circle me-1"></i> إزالة الفلتر</button>
              <button class="dash-filter-btn" @click="filterOpen = !filterOpen">
                <i class="bi bi-sliders me-1"></i> فلترة
              </button>
            </div>
          </div>
          <transition name="filter-slide">
            <div v-if="filterOpen" class="dash-filter-panel mb-4">
              <div class="row g-3 align-items-end">
                <div class="col-12 col-sm-6 col-md-3">
                  <label class="dash-filter-label">من تاريخ</label>
                  <input type="date" v-model="filterDateFrom" class="form-control form-control-sm" />
                </div>
                <div class="col-12 col-sm-6 col-md-3">
                  <label class="dash-filter-label">إلى تاريخ</label>
                  <input type="date" v-model="filterDateTo" class="form-control form-control-sm" />
                </div>
                <div class="col-12 col-sm-6 col-md-3">
                  <label class="dash-filter-label">المنطقة</label>
                  <select ref="regionSelect" v-model="filterRegionId" class="form-select form-select-sm">
                    <option value="">— كل المناطق —</option>
                    <option v-for="r in regionsList" :key="r.id" :value="r.id">{{ r.name }}</option>
                  </select>
                </div>
                <div class="col-12 col-sm-6 col-md-3">
                  <label class="dash-filter-label">المدينة</label>
                  <select
                    ref="citySelect"
                    v-model="filterCityId"
                    class="form-select form-select-sm"
                    :disabled="!filterRegionId"
                  >
                    <option value="">
                      {{ filterRegionId ? '— كل مدن المنطقة —' : '— اختر المنطقة أولاً —' }}
                    </option>
                    <option v-for="c in filteredCitiesList" :key="c.id" :value="c.id">{{ c.name }}</option>
                  </select>
                </div>
                <div class="col-12 d-flex gap-2 justify-content-end">
                  <button class="btn btn-sm btn-primary" @click="applyFilter">
                    <i class="bi bi-check-lg me-1"></i> تطبيق
                  </button>
                  <button class="btn btn-sm btn-outline-secondary" @click="cancelFilter">إلغاء</button>
                </div>
              </div>
            </div>
          </transition>
       
          <!-- ROW 1 — KPI CARDS -->
          <div class="row g-2 mb-4">
            <!-- Card 1: Properties -->
            <div class="col-xl-3 col-md-6">
              <div class="kpi-card kpi-card--blue">
                <div class="kpi-card__top">
                  <div class="kpi-card__icon"><i class="nav-icon bi bi-houses"></i></div>
                  <div>
                    <div class="kpi-card__label" style="font-size:12px;">إجمالي العقارات</div>
                    <div class="kpi-card__value">{{ totalProperties.toLocaleString() }}</div>
                    <div class="kpi-card__month" style="font-size:9px;"><i class="bi bi-plus-circle-fill me-1"></i>{{ newPropertiesThisMonth }} جديد</div>
                  </div>
                </div>
                <div class="kpi-card__bar">
                  <div v-for="seg in propBarSegments" :key="seg.key" class="kpi-bar-seg"
                    :style="{ width: seg.pct + '%', background: seg.color }" :title="seg.label + ': ' + seg.count"></div>
                </div>
                <div class="vertical-pills">
                  <div class="vertical-pill" v-for="status in topStatuses" :key="status.key">
                    <span class="vertical-pill__dot" :style="{ background: status.color }"></span>
                    <span class="vertical-pill__label">{{ status.label }}</span>
                    <span class="vertical-pill__value">{{ status.count }}</span>
                  </div>
                  <div v-if="remainingStatusesCount > 0" class="vertical-pill vertical-pill--more" @click="showStatusesModal = true">
                    <span class="vertical-pill__dot data-dot--neutral"></span>
                    <span class="vertical-pill__label">المزيد</span>
                    <span class="vertical-pill__value"><i class="bi bi-plus-circle"></i> {{ remainingStatusesCount }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card 2: Clients & Providers -->
            <div class="col-xl-3 col-md-6">
              <div class="kpi-card kpi-card--teal">
                <div class="kpi-card__top">
                  <div class="kpi-card__icon"><i class="nav-icon bi bi-person-lines-fill"></i></div>
                  <div>
                    <div class="kpi-card__label" style="font-size:12px;">العملاء</div>
                    <div class="kpi-card__value">{{ usersCount.toLocaleString() }}</div>
                  </div>
                </div>
                <div class="kpi-divider-light"></div>
                <div class="kpi-card__top">
                  <div class="kpi-card__icon kpi-card__icon--sm"><i class="nav-icon fa-solid fa-person-digging"></i></div>
                  <div>
                    <div class="kpi-card__label" style="font-size:12px;">مزودين الخدمة</div>
                    <div class="kpi-card__value kpi-card__value--sm">{{ providersCount.toLocaleString() }}</div>
                  </div>
                </div>
                <div class="vertical-pills">
                  <div class="vertical-pill">
                    <span class="vertical-pill__dot data-dot--positive"></span>
                    <span class="vertical-pill__label">مقبول</span>
                    <span class="vertical-pill__value">{{ providerStats.accepted || 0 }}</span>
                  </div>
                  <div class="vertical-pill">
                    <span class="vertical-pill__dot data-dot--pending"></span>
                    <span class="vertical-pill__label">مراجعة</span>
                    <span class="vertical-pill__value">{{ providerStats.pending || 0 }}</span>
                  </div>
                  <div class="vertical-pill">
                    <span class="vertical-pill__dot data-dot--danger"></span>
                    <span class="vertical-pill__label">مرفوض</span>
                    <span class="vertical-pill__value">{{ providerStats.rejected || 0 }}</span>
                  </div>
                  <div class="vertical-pill">
                    <span class="vertical-pill__dot data-dot--muted"></span>
                    <span class="vertical-pill__label">غير محدد</span>
                    <span class="vertical-pill__value">{{ providerStats.unspecified || 0 }}</span>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card 3: Payments -->
            <div class="col-xl-3 col-md-6">
              <div class="kpi-card kpi-card--indigo">
                <div class="compact-stats">
                  <div class="compact-stat">
                    <div class="compact-stat__icon"><i class="nav-icon fa-solid fa-file-invoice-dollar" style="font-size:10px;"></i></div>
                    <div>
                      <div class="compact-stat__label" style="font-size:12px;">إجمالي الإيرادات</div>
                      <div class="compact-stat__value" style="font-size:12px;">{{ Number(totalCapturedPayments || 0).toLocaleString() }}</div>
                    </div>
                  </div>
                  <div class="compact-stat-divider"></div>
                  <div class="compact-stat">
                    <div class="compact-stat__icon compact-stat__icon--sm" style="font-size:10px;"><i class="bi bi-hash"></i></div>
                    <div>
                      <div class="compact-stat__label" style="font-size:10px;">العمليات الناجحة</div>
                      <div class="compact-stat__value compact-stat__value--sm" style="font-size:12px;">{{ paymentsCount.toLocaleString() }}</div>
                    </div>
                  </div>
                </div>
                <div class="kpi-divider-light"></div>
                <div class="compact-dept-header">
                  <i class="bi bi-diagram-3-fill me-1" style="font-size:10px;"></i>
                  <span style="font-size:10px;">الإيرادات حسب القسم</span>
                </div>
                <div class="compact-dept-list">
                  <div class="compact-dept-row" v-for="item in topDepartments" :key="item.key">
                    <i :class="item.icon" :style="{color: item.iconColor}"></i>
                    <span class="compact-dept-name">{{ item.name }}</span>
                    <span class="compact-dept-val">{{ Number(item.total).toLocaleString() }}</span>
                  </div>
                  <div v-if="remainingDepartmentsCount > 0" class="compact-dept-more" @click="showDepartmentsModal = true" style="font-size:10px;">
                    + {{ remainingDepartmentsCount }} أقسام أخرى
                  </div>
                  <div class="compact-dept-empty" v-if="!Object.keys(capturedPaymentsByModel).length">
                    لا توجد مدفوعات
                  </div>
                </div>
                <div class="kpi-divider-light"></div>
                <div class="compact-actions">
                  <div class="compact-action" @click="$router.push('/financeRequests')">
                    <i class="fa-solid fa-comments-dollar" style="font-size:12px;"></i>
                    <div>
                      <div class="compact-action__label" style="font-size:10px;">طلبات التمويل</div>
                      <div class="compact-action__value" style="font-size:12px;">{{ financeCount.toLocaleString() }}</div>
                    </div>
                  </div>
                  <div class="compact-action" @click="$router.push('/coupons')">
                    <i class="fa-solid fa-percent" style="font-size:12px;"></i>
                    <div>
                      <div class="compact-action__label" style="font-size:10px;">الكوبونات</div>
                      <div class="compact-action__value" style="font-size:12px;">{{ couponsCount.toLocaleString() }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Card 4: Maintenance + Photo Orders -->
            <div class="col-xl-3 col-md-6">
              <div class="kpi-card kpi-card--amber">
                <div class="orders-header">
                  <div class="orders-header__tab" :class="{ 'orders-header__tab--active': ordersTab === 'maintenance' }" @click="ordersTab = 'maintenance'">
                    <div class="kpi-card__icon kpi-card__icon--sm"><i class="bi bi-tools"></i></div>
                    <div>
                      <div class="kpi-card__label">الصيانة</div>
                      <div class="kpi-card__value kpi-card__value--sm">{{ totalOrders.toLocaleString() }}</div>
                    </div>
                  </div>
                  <div class="orders-header__sep"></div>
                  <div class="orders-header__tab" :class="{ 'orders-header__tab--active': ordersTab === 'photos' }" @click="ordersTab = 'photos'">
                    <div class="kpi-card__icon kpi-card__icon--sm"><i class="nav-icon fa-solid fa-camera-retro"></i></div>
                    <div>
                      <div class="kpi-card__label">التصوير</div>
                      <div class="kpi-card__value kpi-card__value--sm">{{ photoOrdersCount.toLocaleString() }}</div>
                    </div>
                  </div>
                </div>
                <div class="maintenance-response-box"
                  v-if="ordersTab === 'maintenance'">

                <div class="maintenance-response-icon">
                  <i class="bi bi-stopwatch"></i>
                </div>

                <div class="maintenance-response-content">
                  <div class="maintenance-response-label">
                    متوسط وقت الاستجابة لطلبات الصيانة
                  </div>

                  <div class="maintenance-response-value">
                    {{ averageResponseTime.hours }} ساعة
                  </div>
                </div>

                </div>

                <div class="orders-horizontal">
                  <template v-if="ordersTab === 'maintenance'">
                    <div class="order-col">
                      <div class="order-col__title">العميل</div>
                      <div class="order-item"><span>انتظار جدية</span><span class="order-item__value order-item__value--warning">{{ orderStats.waiting_deposit || 0 }}</span></div>
                      <div class="order-item"><span>انتظار سداد</span><span class="order-item__value order-item__value--warning">{{ orderStats.waiting_payment || 0 }}</span></div>
                      <div class="order-item"><span>مشكلة جدية</span><span class="order-item__value order-item__value--danger">{{ orderStats.deposit_issue || 0 }}</span></div>
                      <div class="order-item"><span>مشكلة سداد</span><span class="order-item__value order-item__value--danger">{{ orderStats.payment_issue || 0 }}</span></div>
                      <div class="order-item"><span>ملغي</span><span class="order-item__value order-item__value--danger">{{ orderStats.client_canceled || 0 }}</span></div>
                    </div>
                    <div class="order-col">
                      <div class="order-col__title">المزود</div>
                      <div class="order-item"><span>تعيين فني</span><span class="order-item__value order-item__value--info">{{ orderStats.assigned || 0 }}</span></div>
                      <div class="order-item"><span>وصل وبدأ</span><span class="order-item__value order-item__value--success">{{ orderStats.arrived || 0 }}</span></div>
                      <div class="order-item"><span>معلق</span><span class="order-item__value order-item__value--warning">{{ orderStats.hold || 0 }}</span></div>
                      <div class="order-item"><span>لاغي</span><span class="order-item__value order-item__value--danger">{{ orderStats.provier_canceled || 0 }}</span></div>
                    </div>
                    <div class="order-col">
                      <div class="order-col__title">الإدارة</div>
                      <div class="order-item"><span>جديد</span><span class="order-item__value">{{ orderStats.new || 0 }}</span></div>
                      <div class="order-item"><span>مراجعة</span><span class="order-item__value">{{ orderStats.under_review || 0 }}</span></div>
                      <div class="order-item"><span>تم الحل</span><span class="order-item__value">{{ orderStats.done || 0 }}</span></div>
                    </div>
                  </template>
                  <template v-else>
                    <div class="order-col">
                      <div class="order-col__title">العميل</div>
                      <div class="order-item"><span>انتظار جدية</span><span class="order-item__value order-item__value--warning">{{ photoOrderStats.waiting_deposit || 0 }}</span></div>
                      <div class="order-item"><span>انتظار سداد</span><span class="order-item__value order-item__value--warning">{{ photoOrderStats.waiting_payment || 0 }}</span></div>
                      <div class="order-item"><span>مشكلة جدية</span><span class="order-item__value order-item__value--danger">{{ photoOrderStats.deposit_issue || 0 }}</span></div>
                      <div class="order-item"><span>مشكلة سداد</span><span class="order-item__value order-item__value--danger">{{ photoOrderStats.payment_issue || 0 }}</span></div>
                      <div class="order-item"><span>ملغي</span><span class="order-item__value order-item__value--danger">{{ photoOrderStats.client_canceled || 0 }}</span></div>
                    </div>
                    <div class="order-col">
                      <div class="order-col__title">المزود</div>
                      <div class="order-item"><span>تعيين فني</span><span class="order-item__value order-item__value--info">{{ photoOrderStats.assigned || 0 }}</span></div>
                      <div class="order-item"><span>وصل وبدأ</span><span class="order-item__value order-item__value--success">{{ photoOrderStats.arrived || 0 }}</span></div>
                      <div class="order-item"><span>معلق</span><span class="order-item__value order-item__value--warning">{{ photoOrderStats.hold || 0 }}</span></div>
                      <div class="order-item"><span>لاغي</span><span class="order-item__value order-item__value--danger">{{ photoOrderStats.provier_canceled || 0 }}</span></div>
                    </div>
                    <div class="order-col">
                      <div class="order-col__title">الإدارة</div>
                      <div class="order-item"><span>جديد</span><span class="order-item__value">{{ photoOrderStats.new || 0 }}</span></div>
                      <div class="order-item"><span>مراجعة</span><span class="order-item__value">{{ photoOrderStats.under_review || 0 }}</span></div>
                      <div class="order-item"><span>تم الحل</span><span class="order-item__value">{{ photoOrderStats.done || 0 }}</span></div>
                    </div>
                  </template>
                </div>
                <div class="orders-more-btn" @click="showOrderStatusesModal = true" style="font-size:10px;">
                  <i class="bi bi-grid-3x3-gap-fill"></i> عرض جميع التفاصيل
                </div>
              </div>
            </div>
          </div>

          <!-- Property distribution by region -->
          <div class="row g-3 mb-4">
            <div class="col-12">
              <div class="card region-stats-card">
                <div class="card-header">
                  <h3 class="card-title mb-0"><i class="bi bi-geo-alt-fill me-1 text-primary"></i> العقارات حسب المنطقة</h3>
                </div>
                <div class="card-body">
                  <div v-if="propertiesByRegion.length" class="region-stats-grid">
                    <div v-for="region in propertiesByRegion" :key="region.regionId ?? 'unspecified'" class="region-stat">
                      <div class="region-stat__row">
                        <span class="region-stat__name">{{ region.name }}</span>
                        <span class="region-stat__count">{{ Number(region.count).toLocaleString() }} <small>عقار</small></span>
                      </div>
                      <div class="region-stat__track" aria-hidden="true">
                        <span class="region-stat__fill" :style="{ width: (Number(region.count) / regionalMaxCount * 100) + '%' }"></span>
                      </div>
                    </div>
                  </div>
                  <div v-else class="empty-state">{{ isFiltered ? 'لا توجد عقارات تطابق الفلتر المحدد' : 'لا توجد عقارات' }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Card 4: ManagemetReqs -->
          <div class="row g-2 mb-4">
            <div class="col-12">
              <div class="kpi-card kpi-card--violet management-card">
                <div class="management-card__inner">
                  <div class="management-card__head">
                    <div class="kpi-card__icon management-card__icon">
                      <i class="nav-icon fa-solid fa-building-flag"></i>
                    </div>
                    <div>
                      <div class="management-card__title">إدارة الأملاك</div>
                      <div class="management-card__total">{{ managementTotal.toLocaleString() }}</div>
                      <div class="management-card__sub">إجمالي الطلبات</div>
                    </div>
                  </div>
                  <div class="management-card__vsep"></div>
                  <div class="management-card__stats">
                    <div class="management-stat management-stat--yellow">
                      <div class="management-stat__icon"><i class="bi bi-bell-fill"></i></div>
                      <div class="management-stat__val">{{ managementStats.new || 0 }}</div>
                      <div class="management-stat__label">جديد</div>
                    </div>
                    <div class="management-stat management-stat--blue">
                      <div class="management-stat__icon"><i class="bi bi-hourglass-split"></i></div>
                      <div class="management-stat__val">{{ managementStats.in_progress || 0 }}</div>
                      <div class="management-stat__label">قيد التنفيذ</div>
                    </div>
                    <div class="management-stat management-stat--green">
                      <div class="management-stat__icon"><i class="bi bi-check-circle-fill"></i></div>
                      <div class="management-stat__val">{{ managementStats.done || 0 }}</div>
                      <div class="management-stat__label">مكتمل</div>
                    </div>
                    <div class="management-stat management-stat--red">
                      <div class="management-stat__icon text-danger"><i class="bi bi-x-circle-fill"></i></div>
                      <div class="management-stat__val">{{ managementStats.rejected || 0 }}</div>
                      <div class="management-stat__label">مرفوض</div>
                    </div>
                  </div>
                  <div class="management-card__progress">
                    <div class="management-progress-label">نسبة الإنجاز</div>
                    <div class="management-progress-bar">
                      <div class="management-progress-fill" :style="{ width: managementDonePct + '%' }"></div>
                    </div>
                    <div class="management-progress-pct">{{ managementDonePct }}%</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        
          <!-- ROW 2 — MINI STATS DRAWER -->
          <!-- Floating toggle tab pinned to left edge -->
          <div class="mini-drawer-tab" @click="miniStatsOpen = !miniStatsOpen" :class="{ 'mini-drawer-tab--open': miniStatsOpen }">
            <i class="bi" :class="miniStatsOpen ? 'bi-chevron-left' : 'bi-chevron-right'"></i>
            <span class="mini-drawer-tab__label">طلبات سريعة</span>
          </div>

          <!-- Backdrop overlay -->
          <transition name="fade">
            <div v-if="miniStatsOpen" class="mini-drawer-overlay" @click="miniStatsOpen = false"></div>
          </transition>

          <!-- Slide-in drawer panel -->
          <transition name="slide-drawer">
            <div v-if="miniStatsOpen" class="mini-drawer" dir="rtl">
              <div class="mini-drawer__header">
                <div class="mini-drawer__title">
                  <i class="bi bi-grid-1x2-fill me-2 text-primary"></i>
                  الإحصائيات السريعة
                </div>
                <button class="mini-drawer__close" @click="miniStatsOpen = false">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
              <div class="mini-drawer__body">
                <div class="mini-drawer__item" @click="$router.push('/proRequest'); miniStatsOpen = false">
                  <div class="mini-drawer__icon mini-drawer__icon--olive">
                    <i class="fa-solid fa-building-circle-exclamation"></i>
                  </div>
                  <div class="mini-drawer__text">
                    <div class="mini-drawer__val">{{ (propertyRequestsCount || 0).toLocaleString() }}</div>
                    <div class="mini-drawer__lbl">طلب عقار</div>
                  </div>
                  <i class="bi bi-arrow-left mini-drawer__arrow"></i>
                </div>

                <div class="mini-drawer__item" @click="$router.push('/booking'); miniStatsOpen = false">
                  <div class="mini-drawer__icon mini-drawer__icon--green">
                    <i class="fa-solid fa-building-lock"></i>
                  </div>
                  <div class="mini-drawer__text">
                    <div class="mini-drawer__val">{{ (bookingCount || 0).toLocaleString() }}</div>
                    <div class="mini-drawer__lbl">حجز عقار</div>
                  </div>
                  <i class="bi bi-arrow-left mini-drawer__arrow"></i>
                </div>

                <div class="mini-drawer__item" @click="$router.push('/reservations'); miniStatsOpen = false">
                  <div class="mini-drawer__icon mini-drawer__icon--sage">
                    <i class="fa-solid fa-calendar-check"></i>
                  </div>
                  <div class="mini-drawer__text">
                    <div class="mini-drawer__val">{{ (reservationsCount || 0).toLocaleString() }}</div>
                    <div class="mini-drawer__lbl">طلبات المعاينة</div>
                  </div>
                  <i class="bi bi-arrow-left mini-drawer__arrow"></i>
                </div>

                <div class="mini-drawer__item" @click="$router.push('/lawyerReq'); miniStatsOpen = false">
                  <div class="mini-drawer__icon mini-drawer__icon--gold">
                    <i class="fa-solid fa-gavel"></i>
                  </div>
                  <div class="mini-drawer__text">
                    <div class="mini-drawer__val">{{ (lawyerRequestsCount || 0).toLocaleString() }}</div>
                    <div class="mini-drawer__lbl">اطلب محاميك</div>
                  </div>
                  <i class="bi bi-arrow-left mini-drawer__arrow"></i>
                </div>

                <div class="mini-drawer__item" @click="$router.push('/complains'); miniStatsOpen = false">
                  <div class="mini-drawer__icon mini-drawer__icon--danger">
                    <i class="fa-solid fa-face-angry"></i>
                  </div>
                  <div class="mini-drawer__text">
                    <div class="mini-drawer__val">{{ (complainsCount || 0).toLocaleString() }}</div>
                    <div class="mini-drawer__lbl">الشكاوى</div>
                  </div>
                  <i class="bi bi-arrow-left mini-drawer__arrow"></i>
                </div>
                <div class="mini-drawer__item" @click="$router.push('/contactUs'); miniStatsOpen = false">
                  <div class="mini-drawer__icon mini-drawer__icon--ink">
                    <i class="nav-icon fa-solid fa-headset"></i>
                  </div>
                  <div class="mini-drawer__text">
                    <div class="mini-drawer__val">{{ (contactUsTotal || 0).toLocaleString() }}</div>
                    <div class="mini-drawer__lbl">اتصل بنا</div>
                  </div>
                  <i class="bi bi-arrow-left mini-drawer__arrow"></i>
                </div>
              </div>
            </div>
          </transition>
          <div class="row g-2 mb-4">
            <div class="col-12">
              <div class="kpi-card contact-card" style="border-radius:12px;padding:14px 18px;">
                <div class="management-card__inner">

                  <div class="management-card__head">
                    <div class="kpi-card__icon management-card__icon">
                      <i class="nav-icon fa-solid fa-headset"></i>
                    </div>
                    <div>
                      <div class="management-card__title">اتصل بنا</div>
                      <div class="management-card__total">{{ (contactUsTotal || 0).toLocaleString() }}</div>
                      <div class="management-card__sub">إجمالي الطلبات</div>
                    </div>
                  </div>

                  <div class="management-card__vsep"></div>

                  <div class="management-card__stats">
                    <div class="management-stat management-stat--yellow">
                      <div class="management-stat__icon"><i class="bi bi-bell-fill"></i></div>
                      <div class="management-stat__val">{{ contactUsStats.new || 0 }}</div>
                      <div class="management-stat__label">جديد</div>
                    </div>
                    <div class="management-stat management-stat--blue">
                      <div class="management-stat__icon"><i class="bi bi-hourglass-split"></i></div>
                      <div class="management-stat__val">{{ contactUsStats.in_progress || 0 }}</div>
                      <div class="management-stat__label">قيد المعالجة</div>
                    </div>
                    <div class="management-stat management-stat--green">
                      <div class="management-stat__icon"><i class="bi bi-check-circle-fill"></i></div>
                      <div class="management-stat__val">{{ contactUsStats.done || 0 }}</div>
                      <div class="management-stat__label">مكتمل</div>
                    </div>
                    <div class="management-stat management-stat--red">
                      <div class="management-stat__icon"><i class="bi bi-x-circle-fill"></i></div>
                      <div class="management-stat__val">{{ contactUsStats.rejected || 0 }}</div>
                      <div class="management-stat__label">مرفوض</div>
                    </div>
                  </div>

                  <div class="management-card__progress">
                    <div class="management-progress-label">نسبة الإنجاز</div>
                    <div class="management-progress-bar">
                      <div class="management-progress-fill" :style="{ width: contactUsDonePct + '%' }"></div>
                    </div>
                    <div class="management-progress-pct">{{ contactUsDonePct }}%</div>
                  </div>

                </div>
              </div>
            </div>
          </div>
          <!-- PROPERTY DONUT + EXCHANGES DONUT + TREND -->
          <div class="row g-3 mb-4">
            <div class="col-xl-3 col-md-6">
              <div class="card h-100">
                <div class="card-header">
                  <h3 class="card-title"><i class="bi bi-pie-chart-fill me-1 text-primary"></i> حالة العقارات</h3>
                </div>
                <div class="card-body d-flex flex-column align-items-center">
                  <div class="donut-wrap">
                    <PropChart :segments="propBarSegments" />
                    <div class="donut-center">
                      <div class="donut-center__val">{{ totalProperties.toLocaleString() }}</div>
                      <div class="donut-center__lbl">إجمالي</div>
                    </div>
                  </div>
                  <div class="chart-legend mt-3 w-100">
                    <div v-for="seg in propBarSegments" :key="seg.key" class="chart-legend-row">
                      <span class="chart-legend-dot" :style="{ background: seg.color }"></span>
                      <span class="chart-legend-name">{{ seg.label }}</span>
                      <span class="chart-legend-val">{{ seg.count }}</span>
                      <span class="chart-legend-pct">{{ seg.pct }}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-3 col-md-6">
              <div class="card h-100">
                <div class="card-header">
                  <h3 class="card-title"><i class="bi bi-pie-chart me-1 text-warning"></i> حالة التقبيل</h3>
                </div>
                <div class="card-body d-flex flex-column align-items-center">
                  <div class="donut-wrap">
                    <PropChart :segments="exchangeBarSegments" />
                    <div class="donut-center">
                      <div class="donut-center__val">{{ totalExchanges.toLocaleString() }}</div>
                      <div class="donut-center__lbl">إجمالي</div>
                    </div>
                  </div>
                  <div class="chart-legend mt-3 w-100">
                    <div v-for="seg in exchangeBarSegments" :key="seg.key" class="chart-legend-row">
                      <span class="chart-legend-dot" :style="{ background: seg.color }"></span>
                      <span class="chart-legend-name">{{ seg.label }}</span>
                      <span class="chart-legend-val">{{ seg.count }}</span>
                      <span class="chart-legend-pct">{{ seg.pct }}%</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="col-xl-6">
              <div class="card h-100">
                <div class="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h3 class="card-title mb-0">
                    <i class="bi bi-graph-up me-1 text-success"></i>
                    الاتجاه الشهري — آخر {{ trendMonths }} {{ trendMonths === 1 ? 'شهر' : 'أشهر' }}
                  </h3>
                  <div class="d-flex align-items-center gap-3 flex-wrap">
                    <!-- Range selector -->
                    <div class="trend-range-btns">
                      <button
                        v-for="opt in trendRangeOptions" :key="opt.value"
                        class="trend-range-btn"
                        :class="{ 'trend-range-btn--active': trendMonths === opt.value }"
                        @click="changeTrendMonths(opt.value)"
                      >{{ opt.label }}</button>
                    </div>
                    <!-- Legend -->
                    <div class="d-flex gap-3" style="font-size:12px;">
                      <span><span class="chart-legend-dot d-inline-block me-1" style="background:#3e572e;width:10px;height:10px;border-radius:2px;"></span>عقارات جديدة</span>
                      <span><span class="chart-legend-dot d-inline-block me-1" style="background:#a69057;width:10px;height:10px;border-radius:2px;"></span>طلبات التقبيل</span>
                    </div>
                  </div>
                </div>
                <div class="card-body">
                  <TrendChart
                    v-if="monthlyProps.length || monthlyExchanges.length"
                    :labels="trendLabels"
                    :propsData="monthlyPropsData"
                    :exchangesData="monthlyExchangesData"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- MAINTENANCE+PHOTO CHART + PURPOSE DONUT -->
          <div class="row g-3 mb-4">
            <div class="col-xl-7">
              <div class="card">
                <div class="card-header">
                  <h3 class="card-title"><i class="bi bi-bar-chart-fill me-1 text-warning"></i> توزيع الطلبات حسب الحالة</h3>
                </div>
                <div class="card-body">
                  <MaintenanceChart
                    :maintenanceStats="orderStats"
                    :photoStats="photoOrderStats"
                    :statusChoices="orderStatusChoices"
                  />
                </div>
              </div>
            </div>
            <div class="col-xl-5">
              <div class="card">
                <div class="card-header">
                  <h3 class="card-title"><i class="bi bi-pie-chart me-1 text-info"></i> العقارات حسب الغرض</h3>
                </div>
                <div class="card-body d-flex flex-column align-items-center">
                  <PurposeChart
                    v-if="purposeSeries.some(v => v > 0)"
                    :key="purposeSeries.join('-')"
                    :series="purposeSeries"
                  />
                  <div class="chart-legend mt-3 w-100">
                    <div class="chart-legend-row">
                      <span class="chart-legend-dot" style="background:#3e572e;"></span>
                      <span class="chart-legend-name">للبيع</span>
                      <span class="chart-legend-val">{{ (propPurpose.for_sale || 0).toLocaleString() }}</span>
                    </div>
                    <div class="chart-legend-row">
                      <span class="chart-legend-dot" style="background:#a69057;"></span>
                      <span class="chart-legend-name">للإيجار</span>
                      <span class="chart-legend-val">{{ (propPurpose.for_rent || 0).toLocaleString() }}</span>
                    </div>
                    <div class="chart-legend-row">
                      <span class="chart-legend-dot" style="background:#d9822b;"></span>
                      <span class="chart-legend-name">للاستثمار</span>
                      <span class="chart-legend-val">{{ (propPurpose.for_investment || 0).toLocaleString() }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <!-- Filter notice for recent tables -->
          <transition name="filter-slide">
            <div v-if="isFiltered" class="dash-filter-notice mb-3">
              <i class="bi bi-funnel-fill me-2"></i>
             نظراً لإستخدامكم الفلتر
              <strong>{{ filterDescription }}</strong>
              — تظهر فقط السجلات المطابقة للفلتر المحدد
            </div>
          </transition>
          <!-- RECENT PROPERTIES + ORDERS -->
          <div class="row g-3 mb-4">
            <div class="col-xl-6">
              <div class="card">
                <div class="card-header d-flex align-items-center justify-content-between">
                  <h3 class="card-title mb-0"><i class="bi bi-houses me-1 text-primary"></i> أحدث العقارات</h3>
                  <button class="btn btn-sm btn-outline-primary" @click="$router.push('/properties')">عرض الكل <i class="bi bi-arrow-left ms-1"></i></button>
                </div>
                <div class="card-body p-0">
                  <div v-if="!recentProperties.length" class="empty-state">
                    <i class="bi bi-funnel me-1" v-if="isFiltered"></i>
                    {{ isFiltered ? 'لا توجد عقارات تطابق الفلتر المحدد' : 'لا توجد عقارات' }}
                  </div>
                  <table v-else class="dash-table">
                    <thead><tr><th>#</th><th>العنوان</th><th>الغرض</th><th>الحالة</th><th>التاريخ</th></tr></thead>
                    <tbody>
                      <tr v-for="p in recentProperties" :key="p.id">
                        <td><span class="id-link" @click="$router.push('/properties/update/'+p.id)">#{{ p.id }}</span></td>
                        <td class="ellipsis-cell">{{ p.title || 'بدون عنوان' }}</td>
                        <td><span class="badge" :class="purposeClass(p.purpose)">{{ purposeLabel(p.purpose) }}</span></td>
                        <td><span class="badge" :class="propStatusClass(p.status)">{{ propStatusChoices[p.status] || p.status }}</span></td>
                        <td class="date-cell">{{ $shortDate(p.createdAt) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="col-xl-6">
              <div class="card">
                <div class="card-header d-flex align-items-center justify-content-between">
                  <h3 class="card-title mb-0"><i class="bi bi-tools me-1 text-warning"></i> أحدث طلبات الصيانة</h3>
                  <button class="btn btn-sm btn-outline-primary" @click="$router.push('/orders')">عرض الكل <i class="bi bi-arrow-left ms-1"></i></button>
                </div>
                <div class="card-body p-0">
                  <div v-if="!recentOrders.length" class="empty-state">
                    <i class="bi bi-funnel me-1" v-if="isFiltered"></i>
                    {{ isFiltered ? 'لا توجد طلبات تطابق الفلتر المحدد' : 'لا توجد طلبات' }}
                  </div>
                  <table v-else class="dash-table">
                    <thead><tr><th>#</th><th>النوع</th><th>المشكلة</th><th>الحالة</th><th>التاريخ</th></tr></thead>
                    <tbody>
                      <tr v-for="o in recentOrders" :key="o.id">
                        <td><span class="id-link id-link--warn" @click="$router.push('/orders/update/'+o.id)">#{{ o.id }}</span></td>
                        <td><span class="text-muted" style="font-size:12px;">{{ orderTypesChoices[o.orderType] || o.orderType }}</span></td>
                        <td class="ellipsis-cell">{{ o.MaintenanceIssue ? o.MaintenanceIssue.name : '—' }}</td>
                        <td><span class="badge" :class="orderStatusClass(o.status)">{{ orderStatusChoices[o.status] || o.status }}</span></td>
                        <td class="date-cell">{{ $shortDate(o.createdAt) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <!-- Late Rental-->
          <div class="row g-3 mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header d-flex align-items-center justify-content-between">
                  <h3 class="card-title mb-0">
                    <i class="bi bi-exclamation-triangle-fill me-1 text-danger"></i>
                    الإيجارات المتأخرة
                    <span v-if="overdueTotalCount" class="badge bg-warning ms-2" style="font-size:11px;">
                      {{ overdueTotalCount }} دفعة — {{ Number(overdueTotalAmount).toLocaleString() }} ر.س
                    </span>
                  </h3>
                  <button class="btn btn-sm btn-outline-primary" @click="$router.push('/overdueRentals')"
                      >عرض الكل <i class="bi bi-arrow-left ms-1"></i>
                  </button>
                </div>
                <div class="card-body p-0">
                  <div v-if="!overdueRentals.length" class="empty-state">
                    <i class="bi bi-funnel me-1" v-if="isFiltered"></i>
                    {{ isFiltered ? 'لا توجد إيجارات متأخرة تطابق الفلتر المحدد' : 'لا توجد إيجارات متأخرة' }}
                  </div>
                  <table v-else class="dash-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>العقار</th>
                        <th>إجمالي المستحق</th>
                        <th>أقدم استحقاق</th>
                        <th>التأخر</th>
                        <th>الدفعات المتأخرة</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="r in overdueRentals" :key="r.propertyId" class="overdue-row">
                        <td><span class="id-link data-link--danger" @click="$router.push('/properties/update/'+(r.Properties ? r.Properties.id : r.propertyId))">#{{ r.Properties ? r.Properties.id : r.propertyId }}</span></td>
                        <td class="ellipsis-cell">
                          <span v-if="r.Properties">{{ r.Properties.title || 'عقار #' + r.Properties.id }}</span>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td class="data-value--danger" style="white-space:nowrap;font-weight:600;">
                          {{ Number(r.totalAmount).toLocaleString() }} ر.س
                        </td>
                        <td class="date-cell">{{ $shortDate(r.dueDate) }}</td>
                        <td>
                          <span class="overdue-badge" :class="overdueSeverity(r.dueDate)">
                            {{ overdueDays(r.dueDate) }} يوم
                          </span>
                        </td>
                        <td>
                          <span class="overdue-count-badge" :class="r.overdueCount > 3 
                                  ? 'overdue-count-badge--critical'
                                  : r.overdueCount > 1 ? 'overdue-count-badge--warn'
                                  : 'overdue-count-badge--ok'"
                            :title="`${r.overdueCount} دفعة متأخرة`"
                          >
                            {{ r.overdueCount }} دفعة
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <!-- CONTACT US RECENT -->
          <div class="row g-3 mb-4">
            <div class="col-12">
              <div class="card">
                <div class="card-header d-flex align-items-center justify-content-between">
                  <h3 class="card-title mb-0">
                    <i class="nav-icon fa-solid fa-headset data-icon--gold"></i>
                    طلبات اتصل بنا الأخيرة
                  </h3>
                  <button class="btn btn-sm btn-outline-primary" @click="$router.push('/contactUs')">
                    عرض الكل <i class="bi bi-arrow-left ms-1"></i>
                  </button>
                </div>
                <div class="card-body p-0">
                  <div v-if="!recentContactUs.length" class="empty-state">
                    <i class="bi bi-funnel me-1" v-if="isFiltered"></i>
                    {{ isFiltered ? 'لا توجد طلبات تطابق الفلتر المحدد' : 'لا توجد طلبات' }}
                  </div>
                  <table v-else class="dash-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>الاسم</th>
                        <th>رقم الجوال</th>
                        <th>الموضوع</th>
                        <th>الحالة</th>
                        <th>التاريخ</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="c in recentContactUs" :key="c.id">
                        <td>
                          <span class="id-link data-link--gold"
                            @click="$router.push('/contactUs/update/' + c.id)">
                            #{{ c.id }}
                          </span>
                        </td>
                        <td style="font-size:12px;">{{ c.name || '—' }}</td>
                        <td style="font-size:12px;direction:ltr;text-align:right;">{{ c.mobile || '—' }}</td>
                        <td class="ellipsis-cell">{{ c.subject || '—' }}</td>
                        <td>
                          <span class="badge" :class="contactUsStatusClass(c.status)">
                            {{ contactUsStatusChoices[c.status] || c.status }}
                          </span>
                        </td>
                        <td class="date-cell">{{ $shortDate(c.createdAt) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
          <!-- COMPLAINTS + FINANCE -->
          <div class="row g-3 mb-4">
            <div class="col-xl-6">
              <div class="card">
                <div class="card-header d-flex align-items-center justify-content-between">
                  <h3 class="card-title mb-0"><i class="nav-icon fa-solid fa-face-angry me-1 text-danger"></i> الشكاوى الأخيرة</h3>
                  <button class="btn btn-sm btn-outline-primary" @click="$router.push('/complains')">عرض الكل <i class="bi bi-arrow-left ms-1"></i></button>
                </div>
                <div class="card-body p-0">
                  <div v-if="!recentComplains.length" class="empty-state">
                    <i class="bi bi-funnel me-1" v-if="isFiltered"></i>
                    {{ isFiltered ? 'لا توجد شكاوى تطابق الفلتر المحدد' : 'لا توجد شكاوى' }}
                  </div>
                  <table v-else class="dash-table">
                    <thead><tr><th>#</th><th>السبب</th><th>الحالة</th><th>ملاحظة الإدارة</th><th>التاريخ</th></tr></thead>
                    <tbody>
                      <tr v-for="c in recentComplains" :key="c.id">
                        <td><span class="id-link fw-bold data-link--danger" @click="$router.push('/complains/update/'+c.id)">#{{ c.id }}</span></td>
                        <td style="font-size:12px;">{{ c.ComplainReason ? c.ComplainReason.name : '—' }}</td>
                        <td><span class="badge" :class="complainStatusClass(c.status)">{{ complainStatusChoices[c.status] || c.status }}</span></td>
                        <td class="ellipsis-cell" style="max-width:130px;">{{ c.adminNotice || '—' }}</td>
                        <td class="date-cell">{{ $shortDate(c.createdAt) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div class="col-xl-6">
              <div class="card">
                <div class="card-header d-flex align-items-center justify-content-between">
                  <h3 class="card-title mb-0"><i class="nav-icon fa-solid fa-comments-dollar me-1 text-success"></i> طلبات التمويل الأخيرة</h3>
                  <button class="btn btn-sm btn-outline-primary" @click="$router.push('/financeRequests')">عرض الكل <i class="bi bi-arrow-left ms-1"></i></button>
                </div>
                <div class="card-body p-0">
                  <div v-if="!recentFinance.length" class="empty-state">
                    <i class="bi bi-funnel me-1" v-if="isFiltered"></i>
                    {{ isFiltered ? 'لا توجد طلبات تطابق الفلتر المحدد' : 'لا توجد طلبات' }}
                  </div>
                  <table v-else class="dash-table">
                    <thead><tr><th>#</th><th>مقدم الطلب</th><th>المبلغ</th><th>الحالة</th><th>التاريخ</th></tr></thead>
                    <tbody>
                      <tr v-for="f in recentFinance" :key="f.id">
                        <td><span class="id-link fw-bold data-link--positive" @click="$router.push('/financeRequests/update/'+f.id)">#{{ f.id }}</span></td>
                        <td style="font-size:12px;">{{ f.fullName || '—' }}</td>
                        <td style="font-size:12px;white-space:nowrap;">
                          <span v-if="f.amount">{{ Number(f.amount).toLocaleString() }} ر.س</span>
                          <span v-else class="text-muted">—</span>
                        </td>
                        <td><span class="badge" :class="financeStatusClass(f.status)">{{ financeStatusChoices[f.status] || f.status }}</span></td>
                        <td class="date-cell">{{ $shortDate(f.createdAt) }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>

    <!-- MODALS ───────────────────────────────────────────────────────── -->
    <div v-if="showStatusesModal" class="modal-overlay" @click.self="showStatusesModal = false">
      <div class="modal-container">
        <div class="modal-header">
          <h5 class="modal-title">حالات العقارات</h5>
          <button type="button" class="modal-close" @click="showStatusesModal = false">×</button>
        </div>
        <div class="modal-body">
          <div class="statuses-modal-list">
            <div v-for="seg in propBarSegments" :key="seg.key" class="status-modal-row">
              <div class="status-modal-info">
                <span class="chart-legend-dot" :style="{ background: seg.color }"></span>
                <span class="status-modal-name">{{ seg.label }}</span>
              </div>
              <div class="status-modal-count">{{ seg.count }}</div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showStatusesModal = false">إغلاق</button>
        </div>
      </div>
    </div>

    <div v-if="showDepartmentsModal" class="modal-overlay" @click.self="showDepartmentsModal = false">
      <div class="modal-container">
        <div class="modal-header">
          <h5 class="modal-title">تفاصيل الإيرادات حسب القسم</h5>
          <button type="button" class="modal-close" @click="showDepartmentsModal = false">×</button>
        </div>
        <div class="modal-body">
          <div class="departments-modal-list">
            <div class="dept-modal-row" v-for="item in allDepartments" :key="item.key">
              <div class="dept-modal-info">
                <span class="dept-modal-icon" :style="item.iconStyle"><i :class="item.icon" :style="{color: item.iconColor}"></i></span>
                <span class="dept-modal-name">{{ item.name }}</span>
              </div>
              <div class="dept-modal-stats">
                <span class="dept-modal-count">×{{ item.count }}</span>
                <span class="dept-modal-amount">{{ Number(item.total).toLocaleString() }} ر.س</span>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showDepartmentsModal = false">إغلاق</button>
        </div>
      </div>
    </div>

    <div v-if="showOrderStatusesModal" class="modal-overlay" @click.self="showOrderStatusesModal = false">
      <div class="modal-container">
        <div class="modal-header">
          <h5 class="modal-title">حالات الطلبات - {{ ordersTab === 'maintenance' ? 'الصيانة' : 'التصوير' }}</h5>
          <button type="button" class="modal-close" @click="showOrderStatusesModal = false">×</button>
        </div>
        <div class="modal-body">
          <div class="order-statuses-modal">
            <div class="order-status-group">
              <h6>الإدارة</h6>
              <div class="order-status-item"><span>جديد</span><span class="badge bg-secondary">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).new || 0 }}</span></div>
              <div class="order-status-item"><span>مراجعة</span><span class="badge bg-info">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).under_review || 0 }}</span></div>
              <div class="order-status-item"><span>تم الحل</span><span class="badge bg-success">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).done || 0 }}</span></div>
            </div>
            <div class="order-status-group">
              <h6>العميل</h6>
              <div class="order-status-item"><span>انتظار جدية</span><span class="badge bg-warning">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).waiting_deposit || 0 }}</span></div>
              <div class="order-status-item"><span>انتظار سداد</span><span class="badge bg-warning">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).waiting_payment || 0 }}</span></div>
              <div class="order-status-item"><span>مشكلة جدية</span><span class="badge bg-danger">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).deposit_issue || 0 }}</span></div>
              <div class="order-status-item"><span>مشكلة سداد</span><span class="badge bg-danger">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).payment_issue || 0 }}</span></div>
              <div class="order-status-item"><span>لاغي</span><span class="badge bg-danger">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).client_canceled || 0 }}</span></div>
            </div>
            <div class="order-status-group">
              <h6>المزود</h6>
              <div class="order-status-item"><span>تعيين فني</span><span class="badge bg-info">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).assigned || 0 }}</span></div>
              <div class="order-status-item"><span>وصل وبدأ</span><span class="badge bg-primary">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).arrived || 0 }}</span></div>
              <div class="order-status-item"><span>معلق</span><span class="badge bg-warning">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).hold || 0 }}</span></div>
              <div class="order-status-item"><span>لاغي فني</span><span class="badge bg-danger">{{ (ordersTab==='maintenance' ? orderStats : photoOrderStats).provier_canceled || 0 }}</span></div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" @click="showOrderStatusesModal = false">إغلاق</button>
        </div>
      </div>
    </div>

  </main>
</template>

<script>
import { mapGetters } from 'vuex';
import PropChart        from '../../components/PropChart.vue';
import TrendChart       from '../../components/TrendChart.vue';
import PurposeChart     from '../../components/PurposeChart.vue';
import MaintenanceChart from '../../components/MaintenanceChart.vue';
import $ from 'jquery';
window.$ = $;
window.jQuery = $;
import 'select2';
import 'select2/dist/css/select2.min.css';

const PROP_STATUS_CLASS = {
  available: 'bg-success', sold: 'bg-primary', rented: 'bg-info text-dark',
  under_review: 'bg-warning text-dark', rejected: 'bg-danger', reserved: 'bg-secondary',
};
const ORDER_STATUS_CLASS = {
  new: 'bg-secondary', assigned: 'bg-info text-dark', arrived: 'bg-primary',
  done: 'bg-success', hold: 'bg-warning text-dark', client_canceled: 'bg-danger',
  provier_canceled: 'bg-danger', waiting_deposit: 'bg-warning text-dark',
  deposit_issue: 'bg-danger', waiting_payment: 'bg-warning text-dark',
  payment_issue: 'bg-danger', under_review: 'bg-info text-dark',
};
const PROP_SEGMENTS = [
  { key: 'available',    label: 'متاح',        color: '#4f6a3e' },
  { key: 'sold',         label: 'مباع',         color: '#a69057' },
  { key: 'rented',       label: 'مؤجر',         color: '#7f946c' },
  { key: 'under_review', label: 'تحت المراجعة', color: '#d9822b' },
  { key: 'reserved',     label: 'محجوز',        color: '#c7b984' },
  { key: 'rejected',     label: 'مرفوض',        color: '#b85c4a' },
  { key: 'stopped',      label: 'إيقاف من الهيئة', color: '#8a6275' },
];
const EXCHANGE_SEGMENTS = [
  { key: 'available',    label: 'متاح',        color: '#6f8a5a' },
  { key: 'under_review', label: 'تحت المراجعة', color: '#d9822b' },
  { key: 'done',         label: 'مكتمل',        color: '#3e572e' },
  { key: 'rejected',     label: 'مرفوض',        color: '#b85c4a' },
];

export default {
  name: 'HomeView',
  components: { PropChart, TrendChart, PurposeChart, MaintenanceChart },

  data() {
    return {
      mainLoading: true,
      ordersTab: 'maintenance',

      // mini stats drawer state ────────────────────────────────
      miniStatsOpen: false,

      usersCount: 0, providersCount: 0, paymentsCount: 0, exchangesCount: 0,
      totalCapturedPayments: 0, financeCount: 0, reservationsCount: 0,
      complainsCount: 0, couponsCount: 0, newOrdersCount: 0,
      newPropertiesThisMonth: 0, photoOrdersCount: 0,
      capturedPaymentsByModel: {},
      managementStats: { new: 0, in_progress: 0, rejected: 0, done: 0 },
      managementTotal: 0,
      exchangesStatsByStatus: { under_review: 0, available: 0, rejected: 0, done: 0 },
      overdueRentals: [],
      overdueTotalAmount: 0,
      overdueTotalCount: 0,
      propStats:      { available: 0, under_review: 0, sold: 0, rented: 0, reserved: 0, rejected: 0, stopped: 0 },
      propPurpose:    { for_sale: 0, for_rent: 0, for_investment: 0 },
      propertiesByRegion: [],
      orderStats:     {},
      photoOrderStats:{},
      providerStats:  { unspecified: 0, pending: 0, accepted: 0, rejected: 0 },
      recentProperties: [], recentOrders: [], recentComplains: [], recentFinance: [],
      monthlyProps: [], monthlyExchanges: [],
      propStatusChoices: {}, orderStatusChoices: {}, orderTypesChoices: {},
      financeStatusChoices: {}, complainStatusChoices: {},
      showStatusesModal: false,
      showDepartmentsModal: false,
      showOrderStatusesModal: false,
      contactUsTotal: 0,
      contactUsStats:  { new: 0, in_progress: 0, done: 0, rejected: 0 },
      recentContactUs: [],
      contactUsStatusChoices: {}, 
      trendMonths: 12,
      averageResponseTime: {
        seconds:0,
        minutes:0,
        hours:0,
      },
      // ── Dashboard filters
      filterDateFrom: '',
      filterDateTo: '',
      filterCityId: '',
      filterRegionId: '',
      citiesList: [],
      regionsList: [],
      filterOpen: false,
      appliedDateFrom: '',
      appliedDateTo: '',
      appliedCityId: '',
      appliedRegionId: '',
    };
  },

  computed: {
    ...mapGetters(['MEDIA_URL']),

    purposeSeries() {
      return [
        this.propPurpose.for_sale || 0,
        this.propPurpose.for_rent || 0,
        this.propPurpose.for_investment || 0,
      ].map(v => v === 0 ? 0.0001 : v);
    },
    totalProperties() {
      return Object.values(this.propStats).reduce((a, b) => a + (b || 0), 0);
    },
    regionalMaxCount() {
      return Math.max(1, ...this.propertiesByRegion.map(region => Number(region.count || 0)));
    },
    totalOrders() {
      return Object.values(this.orderStats).reduce((a, b) => a + (parseInt(b) || 0), 0);
    },
    propBarSegments() {
      const total = this.totalProperties || 1;
      return PROP_SEGMENTS.map(s => ({
        ...s,
        count: this.propStats[s.key] || 0,
        pct: Math.round(((this.propStats[s.key] || 0) / total) * 100),
      }));
    },
    totalExchanges() {
      return Object.values(this.exchangesStatsByStatus).reduce((a, b) => a + (b || 0), 0);
    },
    exchangeBarSegments() {
      const total = this.totalExchanges || 1;
      return EXCHANGE_SEGMENTS.map(s => ({
        ...s,
        count: this.exchangesStatsByStatus[s.key] || 0,
        pct: Math.round(((this.exchangesStatsByStatus[s.key] || 0) / total) * 100),
      }));
    },
    managementDonePct() {
      if (!this.managementTotal) return 0;
      return Math.round(((this.managementStats.done || 0) / this.managementTotal) * 100);
    },
    topStatuses() {
      return this.propBarSegments.filter(s => s.count > 0).sort((a, b) => b.count - a.count).slice(0, 3);
    },
    remainingStatusesCount() {
      return Math.max(0, this.propBarSegments.filter(s => s.count > 0).length - 3);
    },
    departmentMap() {
      return {
        'MaintenanceOrders':  { name: 'الصيانة والتصوير',  icon: 'bi bi-tools',            iconColor: '#b67b2c', iconStyle: 'background:rgba(166,144,87,.18);' },
        'Properties':         { name: 'إعلان مميز',         icon: 'bi bi-houses',            iconColor: '#3e572e', iconStyle: 'background:rgba(62,87,46,.16);' },
        'PropReservations':   { name: 'حجز عقار',           icon: 'bi bi-building-lock',     iconColor: '#6f8a5a', iconStyle: 'background:rgba(111,138,90,.18);' },
        'Reservations':       { name: 'معاينة فاخرة',       icon: 'bi bi-calendar-check',    iconColor: '#8c805c', iconStyle: 'background:rgba(140,128,92,.18);' },
        'LawyerRequests':     { name: 'طلب محامي',          icon: 'bi bi-hammer',            iconColor: '#c26f2b', iconStyle: 'background:rgba(217,130,43,.16);' },
        'PropertyDocuments':  { name: 'وثائق عقار',         icon: 'bi bi-file-earmark-text', iconColor: '#56664c', iconStyle: 'background:rgba(86,102,76,.16);' },
        'PropertyPayments':   { name: 'دفعات إيجار',        icon: 'bi bi-cash-stack',         iconColor: '#8e743b', iconStyle: 'background:rgba(142,116,59,.17);' },
        'AdLicenseRequests':  { name: 'إصدار الترخيص الإعلاني', icon: 'bi bi-file-earmark-check', iconColor: '#5f6f87', iconStyle: 'background:rgba(95,111,135,.16);' },
        'UserPackages':       { name: 'اشتراكات باقات الصيانة', icon: 'bi bi-box-seam',         iconColor: '#607d6b', iconStyle: 'background:rgba(96,125,107,.17);' },
        'UsersFeatured':      { name: 'تميز عميل',          icon: 'bi bi-star-fill',          iconColor: '#9b735b', iconStyle: 'background:rgba(155,115,91,.16);' },
        'Users':              { name: 'شحن محفظة',          icon: 'bi bi-wallet2',            iconColor: '#4f6a3e', iconStyle: 'background:rgba(79,106,62,.16);' },
      };
    },
    allDepartments() {
      const paymentsByModel = {
        AdLicenseRequests: { total: 0, count: 0 },
        ...this.capturedPaymentsByModel,
      };
      return Object.entries(paymentsByModel)
        .map(([key, value]) => {
          const dept = this.departmentMap[key] || { name: key, icon: 'bi bi-question-circle', iconColor: '#81887c', iconStyle: 'background:rgba(129,136,124,.16);' };
          return { key, ...value, ...dept };
        })
        .sort((a, b) => b.total - a.total);
    },
    topDepartments() { return this.allDepartments.filter(item => item.count > 0).slice(0, 3); },
    remainingDepartmentsCount() { return Math.max(0, this.allDepartments.length - this.topDepartments.length); },
    trendKeys() {
      const months = [];
      for (let i = this.trendMonths - 1; i >= 0; i--) { 
        const d = new Date(); d.setMonth(d.getMonth() - i);
        months.push(d.toISOString().slice(0, 7));
      }
      return months;
    },
    trendLabels() {
      const AR = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];
      return this.trendKeys.map(m => { const [y, mo] = m.split('-'); return AR[parseInt(mo)-1] + ' ' + y; });
    },
    monthlyPropsData() {
      return this.trendKeys.map(m => { const f = this.monthlyProps.find(r => r.month === m); return f ? Number(f.count) : 0; });
    },
    monthlyExchangesData() {
      return this.trendKeys.map(m => { const f = this.monthlyExchanges.find(r => r.month === m); return f ? Number(f.count) : 0; });
    },
    contactUsDonePct() {
      if (!this.contactUsTotal) return 0;
      return Math.round(((this.contactUsStats.done || 0) / this.contactUsTotal) * 100);
    },
    trendRangeOptions() {
      return [
        { label: '3 أشهر',  value: 3  },
        { label: '6 أشهر',  value: 6  },
        { label: 'سنة',      value: 12 },
        { label: 'سنتان',    value: 24 },
      ];
    },
    isFiltered() {
      return !!(
        this.appliedDateFrom ||
        this.appliedDateTo ||
        this.appliedCityId ||
        this.appliedRegionId
      );
    },
    filterDescription() {
      const parts = [];
      if (this.appliedDateFrom) parts.push(`من ${this.appliedDateFrom}`);
      if (this.appliedDateTo)   parts.push(`إلى ${this.appliedDateTo}`);
      if (this.appliedRegionId) parts.push((this.regionsList.find(r => r.id == this.appliedRegionId) || {}).name);
      if (this.appliedCityId)   parts.push(this.cityNameById(this.appliedCityId));
      return parts.filter(Boolean).join(' — ');
    },
    filteredCitiesList() {
      if (!this.filterRegionId) return [];
      return this.citiesList.filter(city => city.regionId == this.filterRegionId);
    },
  },

  methods: {
    propStatusClass:  s => PROP_STATUS_CLASS[s]  || 'bg-light text-dark',
    orderStatusClass: s => ORDER_STATUS_CLASS[s] || 'bg-light text-dark',
    purposeClass: p => ({ for_sale:'bg-primary', for_rent:'bg-success', for_investment:'bg-warning text-dark' }[p] || 'bg-secondary'),
    purposeLabel: p => ({ for_sale:'للبيع', for_rent:'للإيجار', for_investment:'للاستثمار' }[p] || p),
    complainStatusClass: s => ({ new:'bg-warning text-dark', in_progress:'bg-info text-dark', done:'bg-success', rejected:'bg-danger' }[s] || 'bg-light text-dark'),
    financeStatusClass:  s => ({ new:'bg-warning text-dark', in_progress:'bg-info text-dark', success:'bg-success', rejected:'bg-danger' }[s] || 'bg-light text-dark'),
    contactUsStatusClass(s) {
      return {
        new: "bg-warning text-dark",
        in_progress: "bg-info text-dark",
        done: "bg-success",
        rejected: "bg-danger",
      }[s] || "bg-light text-dark";
    },
    cityNameById(cityId) {
      return (this.citiesList.find(city => city.id == cityId) || {}).name || '';
    },
    overdueDays(dueDate) {
      const diff = new Date() - new Date(dueDate);
      return Math.floor(diff / (1000 * 60 * 60 * 24));
    },
    overdueSeverity(dueDate) {
      const days = this.overdueDays(dueDate);
      if (days > 30) return 'overdue-badge--critical';
      if (days > 7)  return 'overdue-badge--high';
      return 'overdue-badge--medium';
    },
    applyFilter() {
      this.appliedCityId = this.filterCityId;
      this.appliedRegionId = this.filterRegionId;
      this.appliedDateFrom = this.filterDateFrom;
      this.appliedDateTo = this.filterDateTo;

      this.filterOpen = false;
      this.mainLoading = true;
      this.fetchDashboard().finally(() => {
        this.mainLoading = false;
      });
    },
    cancelFilter() {
      this.filterDateFrom = this.appliedDateFrom;
      this.filterDateTo = this.appliedDateTo;
      this.filterRegionId = this.appliedRegionId;
      this.filterCityId = this.appliedCityId;
      this.filterOpen = false;
    },
    clearFilter() {
      this.filterDateFrom = '';
      this.filterDateTo = '';
      this.filterCityId = '';
      this.filterRegionId = '';
      this.appliedDateFrom = '';
      this.appliedDateTo = '';
      this.appliedCityId = '';
      this.appliedRegionId = '';
      this.filterOpen = false;
      this.mainLoading = true;
      this.fetchDashboard().finally(() => { this.mainLoading = false; });
    },
    changeTrendMonths(months) {
      this.trendMonths = months;
      this.fetchDashboard();
    },
    async fetchDashboard() {
      try {
        const { data } = await this.$http.get('/api/admin/home/stats', {
          params: {
            months: this.trendMonths,
            dateFrom: this.appliedDateFrom || undefined,
            dateTo: this.appliedDateTo || undefined,
            cityId: this.appliedCityId || undefined,
            regionId: this.appliedRegionId || undefined,
          }
        });
        Object.assign(this, {
          usersCount:               data.usersCount || 0,
          providersCount:           data.providersCount || 0,
          paymentsCount:            data.paymentsCount || 0,
          exchangesCount:           data.exchangesCount || 0,
          totalCapturedPayments:    data.totalCapturedPayments || 0,
          capturedPaymentsByModel:  data.capturedPaymentsByModel || {},
          managementStats:          data.managementStats || { new: 0, in_progress: 0, rejected: 0, done: 0 },
          managementTotal:          data.managementTotal || 0,
          exchangesStatsByStatus:   data.exchangesStatsByStatus || {},
          overdueRentals:           data.overdueRentals || [],
          overdueTotalAmount:       data.overdueTotalAmount || 0,
          overdueTotalCount:        data.overdueTotalCount || 0,
          financeCount:             data.financeCount || 0,
          reservationsCount:        data.reservationsCount || 0,
          complainsCount:           data.complainsCount || 0,
          couponsCount:             data.couponsCount || 0,
          newOrdersCount:           data.newOrdersCount || 0,
          newPropertiesThisMonth:   data.newPropertiesThisMonth || 0,
          photoOrdersCount:         data.photoOrdersCount || 0,
          propertyRequestsCount:    data.propertyRequestsCount || 0,
          bookingCount:             data.bookingCount || 0,
          lawyerRequestsCount:      data.lawyerRequestsCount || 0,
          propStats:                data.propStats || {},
          propPurpose:              data.propPurpose || {},
          propertiesByRegion:       data.propertiesByRegion || [],
          orderStats:               data.orderStats || {},
          photoOrderStats:          data.photoOrderStats || {},
          providerStats:            data.providerStats || {},
          recentProperties:         data.recentProperties || [],
          recentOrders:             data.recentOrders || [],
          recentComplains:          data.recentComplains || [],
          recentFinance:            data.recentFinance || [],
          monthlyProps:             data.monthlyProps || [],
          monthlyExchanges:         data.monthlyExchanges || [],
          propStatusChoices:        data.propertyStatusChoices || {},
          orderStatusChoices:       data.orderStatusChoices || {},
          orderTypesChoices:        data.orderTypesChoices || {},
          financeStatusChoices:     data.financeStatusChoices || {},
          complainStatusChoices:    data.complainStatusChoices || {},
          contactUsTotal:           data.contactUsTotal  || 0,
          contactUsStats:           data.contactUsStats  || 
          { new: 0, in_progress: 0, done: 0, rejected: 0 },
          recentContactUs:          data.recentContactUs || [],
          contactUsStatusChoices:   data.contactUsStatusChoices || {},
          trendMonths:              data.trendMonths || this.trendMonths,
          citiesList:               data.citiesList  || this.citiesList,
          regionsList:              data.regionsList || this.regionsList,
          averageResponseTime:      data.averageResponseTime || {
                                                                  seconds:0,
                                                                  minutes:0,
                                                                  hours:0,
          },
        });
      } catch (err) {
        console.error('Dashboard stats error:', err);
      }
    },

    // Close drawer on Escape key ──────────────────────────────────────
    handleKeydown(e) {
      if (e.key === 'Escape' && this.miniStatsOpen) {
        this.miniStatsOpen = false;
      }
    },

    applySelect2(refName, modelName) {
      const el = this.$refs[refName];
      if (!el) return;
      const vm = this;
      const $el = $(el);
      if ($el.hasClass('select2-hidden-accessible')) {
        $el.off(`change.${refName}`);
        $el.select2('destroy');
      }
      $el
        .select2({
          width: '100%',
          placeholder: 'اختر...',
        })
        .on(`change.${refName}`, function () {
          vm[modelName] = $(this).val() || '';
        });
      $el.val(this[modelName]).trigger('change.select2');
    }
  },

  mounted() {
    this.fetchDashboard().finally(() => { this.mainLoading = false; });
    document.addEventListener('keydown', this.handleKeydown);
    this.$nextTick(() => {
      this.applySelect2('citySelect', 'filterCityId');
      this.applySelect2('regionSelect', 'filterRegionId');
    });
  },

  beforeUnmount() {
    ['citySelect', 'regionSelect'].forEach(ref => {
      const el = this.$refs[ref];
      if (!el) return;

      const $el = $(el);
      if ($el.hasClass('select2-hidden-accessible')) {
        $el.off(`change.${ref}`);
        $el.select2('destroy');
      }
    });
  },
  watch: {
    filterRegionId(newRegionId, oldRegionId) {
      if (newRegionId == oldRegionId) return;
      const selectedCity = this.citiesList.find(city => city.id == this.filterCityId);
      if (!selectedCity || selectedCity.regionId != newRegionId) {
        this.filterCityId = '';
      }
      this.$nextTick(() => this.applySelect2('citySelect', 'filterCityId'));
    },
    filterOpen(val) {
      if (val) {
        this.$nextTick(() => {
          this.applySelect2('citySelect', 'filterCityId');
          this.applySelect2('regionSelect', 'filterRegionId');
        });
      }
    }
  }
};
</script>

<style scoped>
/* Base ────────────────────────────────────────────────────────────── */
.dash-loading { display:flex; flex-direction:column; align-items:center; justify-content:center; min-height:300px; }

/* KPI Cards ───────────────────────────────────────────────────────── */
.kpi-card { border-radius:12px; padding:10px 12px; color:#fff; height:100%; display:flex; flex-direction:column; }
.kpi-card--blue   { background:linear-gradient(135deg,#3e572e,#273a20); }
.kpi-card--teal   { background:linear-gradient(135deg,#61794c,#3e572e); }
.kpi-card--indigo { background:linear-gradient(135deg,#1c191a,#3b4531); }
.kpi-card--amber  { background:linear-gradient(135deg,#a69057,#6f6039); }
.kpi-card--violet { background:linear-gradient(135deg,#3e572e,#1c191a); }

.kpi-card__top { display:flex; align-items:center; gap:8px; margin-bottom:6px; }
.kpi-card__icon { width:32px; height:32px; border-radius:8px; background:rgba(255,255,255,.18); display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.kpi-card__icon--sm { width:26px; height:26px; font-size:13px; }
.kpi-card__label { font-size:10px; opacity:.78; margin-bottom:1px; }
.kpi-card__value { font-size:20px; font-weight:700; line-height:1.1; }
.kpi-card__value--sm { font-size:15px; }
.kpi-card__month { font-size:9px; opacity:.75; margin-top:2px; }
.kpi-divider { border-top:1px solid rgba(255,255,255,.2); margin:6px 0; }
.kpi-divider-light { border-top:1px solid rgba(255,255,255,.1); margin:5px 0; }
.kpi-card__bar { display:flex; height:3px; border-radius:3px; overflow:hidden; gap:1px; margin:6px 0; }
.kpi-bar-seg { height:100%; transition:width .4s; }

/* Vertical Pills ──────────────────────────────────────────────────── */
.vertical-pills { display:flex; flex-direction:column; gap:4px; margin-top:6px; flex:1; }
.vertical-pill { display:flex; align-items:center; gap:8px; background:rgba(255,255,255,.1); border-radius:6px; padding:4px 8px; font-size:11px; }
.vertical-pill--more { cursor:pointer; background:rgba(255,255,255,.08); transition:all .2s; }
.vertical-pill--more:hover { background:rgba(255,255,255,.2); transform:translateX(-2px); }
.vertical-pill__dot { width:7px; height:7px; border-radius:50%; flex-shrink:0; }
.vertical-pill__label { flex:1; font-size:11px; opacity:.9; }
.vertical-pill__value { font-weight:700; font-size:12px; background:rgba(0,0,0,.2); padding:1px 6px; border-radius:10px; min-width:35px; text-align:center; }

/* Compact Card 3 ──────────────────────────────────────────────────── */
.compact-stats { display:flex; align-items:center; gap:8px; margin-bottom:4px; }
.compact-stat { display:flex; align-items:center; gap:5px; flex:1; }
.compact-stat__icon { width:26px; height:26px; border-radius:6px; background:rgba(255,255,255,.15); display:flex; align-items:center; justify-content:center; font-size:13px; flex-shrink:0; }
.compact-stat__icon--sm { width:22px; height:22px; font-size:11px; }
.compact-stat__label { font-size:8px; opacity:.7; margin-bottom:1px; }
.compact-stat__value { font-size:13px; font-weight:700; line-height:1.2; }
.compact-stat__value--sm { font-size:11px; }
.compact-stat-divider { width:1px; height:24px; background:rgba(255,255,255,.2); }
.compact-dept-header { font-size:8px; opacity:.6; text-transform:uppercase; margin-bottom:4px; display:flex; align-items:center; gap:4px; }
.compact-dept-list { display:flex; flex-direction:column; gap:3px; margin-bottom:4px; flex:1; min-height:60px; }
.compact-dept-row { display:flex; align-items:center; gap:5px; background:rgba(255,255,255,.08); border-radius:4px; padding:3px 5px; }
.compact-dept-name { flex:1; font-size:9px; opacity:.9; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
.compact-dept-val { font-size:9px; font-weight:600; white-space:nowrap; }
.compact-dept-more { font-size:8px; opacity:.6; text-align:center; padding:2px; cursor:pointer; background:rgba(255,255,255,.06); border-radius:3px; margin-top:2px; }
.compact-dept-more:hover { opacity:1; background:rgba(255,255,255,.12); }
.compact-dept-empty { font-size:9px; opacity:.5; text-align:center; padding:4px; }
.compact-actions { display:flex; align-items:center; gap:5px; margin-top:4px; }
.compact-action { display:flex; align-items:center; gap:4px; flex:1; cursor:pointer; background:rgba(255,255,255,.08); border-radius:5px; padding:3px 6px; transition:all .2s; }
.compact-action:hover { background:rgba(255,255,255,.15); transform:translateY(-1px); }
.compact-action i { font-size:11px; opacity:.8; }
.compact-action__label { font-size:7px; opacity:.6; line-height:1; }
.compact-action__value { font-size:11px; font-weight:700; line-height:1.2; }

/* Orders Card ─────────────────────────────────────────────────────── */
.orders-header { display:flex; align-items:center; margin-bottom:8px; background:rgba(255,255,255,.12); border-radius:8px; padding:4px 6px; flex-shrink:0; }
.orders-header__tab { display:flex; align-items:center; gap:5px; flex:1; cursor:pointer; opacity:.6; transition:opacity .15s; padding:2px 4px; border-radius:6px; }
.orders-header__tab:hover { opacity:.85; }
.orders-header__tab--active { opacity:1; background:rgba(255,255,255,.18); }
.orders-header__sep { width:1px; height:26px; background:rgba(255,255,255,.25); margin:0 4px; }
.orders-horizontal { display:grid; grid-template-columns:1fr 1fr 1fr; gap:4px; flex:1; margin:4px 0; }
.order-col { background:rgba(255,255,255,.05); border-radius:6px; padding:5px; }
.order-col__title { font-size:9px; font-weight:600; opacity:.8; margin-bottom:6px; padding-bottom:3px; border-bottom:1px solid rgba(255,255,255,.15); text-align:center; }
.order-item { display:flex; align-items:center; justify-content:space-between; margin-bottom:4px; font-size:9px; }
.order-item span:first-child { opacity:.8; }
.order-item__value { font-weight:700; background:rgba(0,0,0,.2); padding:1px 5px; border-radius:8px; min-width:25px; text-align:center; }
.order-item__value--warning { background:rgba(166,144,87,.28); color:#f1e2b6; }
.order-item__value--danger  { background:rgba(184,92,74,.27); color:#ffd7cf; }
.order-item__value--success { background:rgba(127,154,106,.28); color:#e2f0d8; }
.order-item__value--info    { background:rgba(143,162,124,.25); color:#edf4e7; }
.orders-more-btn { margin-top:6px; padding:4px 8px; background:rgba(255,255,255,.1); border-radius:5px; text-align:center; cursor:pointer; font-size:9px; transition:all .2s; display:flex; align-items:center; justify-content:center; gap:4px; flex-shrink:0; }
.orders-more-btn:hover { background:rgba(255,255,255,.2); transform:translateY(-1px); }

/* Management Card ─────────────────────────────────────────────────── */
.management-card { padding:14px 18px; }
.management-card__inner { display:flex; align-items:center; gap:20px; flex-wrap:wrap; }
.management-card__head { display:flex; align-items:center; gap:12px; min-width:160px; }
.management-card__icon { width:44px; height:44px; font-size:20px; flex-shrink:0; }
.management-card__title { font-size:13px; opacity:.8; margin-bottom:2px; }
.management-card__total { font-size:28px; font-weight:700; line-height:1; }
.management-card__sub { font-size:10px; opacity:.6; margin-top:2px; }
.management-card__vsep { width:1px; height:60px; background:rgba(255,255,255,.2); flex-shrink:0; }
.management-card__stats { display:flex; gap:12px; flex:1; flex-wrap:wrap; }
.management-stat { display:flex; flex-direction:column; align-items:center; gap:4px; background:rgba(255,255,255,.1); border-radius:10px; padding:10px 16px; cursor:pointer; transition:all .2s; min-width:80px; flex:1; }
.management-stat:hover { background:rgba(255,255,255,.2); transform:translateY(-2px); }
.management-stat__icon { font-size:16px; }
.management-stat--yellow .management-stat__icon { color:#e3c77e; }
.management-stat--blue   .management-stat__icon { color:#bdcdaf; }
.management-stat--green  .management-stat__icon { color:#cfe2c0; }
.management-stat--red    .management-stat__icon { color:#edb0a2; }
.management-stat__val { font-size:22px; font-weight:700; }
.management-stat__label { font-size:10px; opacity:.75; }
.management-card__progress { display:flex; align-items:center; gap:8px; min-width:160px; }
.management-progress-label { font-size:10px; opacity:.7; white-space:nowrap; }
.management-progress-bar { flex:1; height:6px; background:rgba(255,255,255,.2); border-radius:3px; overflow:hidden; min-width:80px; }
.management-progress-fill { height:100%; background:#f5f5f5; border-radius:3px; transition:width .6s ease; }
.management-progress-pct { font-size:12px; font-weight:700; white-space:nowrap; }

/* MINI STATS DRAWER */
/* Floating tab pinned to left edge of screen */
.mini-drawer-tab {
  position: fixed;
  left: 0;
  top: 50%;
  transform: translateY(-50%);
  z-index: 600;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-left: none;
  border-radius: 0 10px 10px 0;
  padding: 12px 7px;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 7px;
  box-shadow: 3px 0 12px rgba(0,0,0,.08);
  transition: background .2s, box-shadow .2s;
  user-select: none;
}
.mini-drawer-tab:hover {
  background: #f3f4f6;
  box-shadow: 3px 0 18px rgba(0,0,0,.13);
}
.mini-drawer-tab--open { background: #ebf4df; border-color: rgba(62,87,46,.25); }
.mini-drawer-tab i {
  font-size: 14px;
  color: #3e572e;
  transition: transform .3s;
}
.mini-drawer-tab__label {
  writing-mode: vertical-rl;
  text-orientation: mixed;
  font-size: 11px;
  font-weight: 600;
  color: #4b5563;
  letter-spacing: .05em;
}

/* Semi-transparent backdrop */
.mini-drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0,0,0,.18);
  backdrop-filter: blur(2px);
  z-index: 700;
}

/* Slide-in panel */
.mini-drawer {
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 250px;
  background: #fff;
  border-right: 1px solid #e5e7eb;
  z-index: 800;
  display: flex;
  flex-direction: column;
  box-shadow: 4px 0 30px rgba(0,0,0,.12);
  overflow: hidden;
}

.mini-drawer__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px 12px;
  border-bottom: 1px solid #f3f4f6;
  flex-shrink: 0;
}
.mini-drawer__title {
  font-size: 14px;
  font-weight: 600;
  color: #111827;
  display: flex;
  align-items: center;
}
.mini-drawer__close {
  background: none;
  border: none;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  font-size: 13px;
  transition: all .2s;
}
.mini-drawer__close:hover { background: #f2ddd7; color: #a95142; }

.mini-drawer__body {
  flex: 1;
  overflow-y: auto;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

/* Each mini-stat row inside the drawer */
.mini-drawer__item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  background: #f9fafb;
  border: 1px solid #f3f4f6;
  border-radius: 10px;
  cursor: pointer;
  transition: all .18s;
}
.mini-drawer__item:hover {
  background: #fff;
  border-color: #e5e7eb;
  box-shadow: 0 2px 8px rgba(0,0,0,.07);
  transform: translateX(3px);
}
.mini-drawer__icon {
  width: 36px;
  height: 36px;
  border-radius: 9px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 15px;
  flex-shrink: 0;
}
.mini-drawer__text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.mini-drawer__val {
  font-size: 17px;
  font-weight: 700;
  color: #111827;
  line-height: 1.1;
}
.mini-drawer__lbl {
  font-size: 11px;
  color: #6b7280;
}
.mini-drawer__arrow {
  font-size: 13px;
  color: #d1d5db;
  transition: color .2s;
}
.mini-drawer__item:hover .mini-drawer__arrow {
  color: #9ca3af;
}

/* Vue transition: drawer slides in from left */
.slide-drawer-enter-active,
.slide-drawer-leave-active {
  transition: transform .3s cubic-bezier(.4,0,.2,1);
}
.slide-drawer-enter-from,
.slide-drawer-leave-to {
  transform: translateX(-100%);
}

/* Vue transition: overlay fades */
.fade-enter-active,
.fade-leave-active {
  transition: opacity .25s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Charts & Donuts ─────────────────────────────────────────────────── */
.donut-wrap { position:relative; width:170px; height:170px; flex-shrink:0; }
.donut-center { position:absolute; top:50%; left:50%; transform:translate(-50%,-50%); text-align:center; pointer-events:none; }
.donut-center__val { font-size:18px; font-weight:700; color:#111827; line-height:1; }
.donut-center__lbl { font-size:9px; color:#9ca3af; margin-top:2px; }
.chart-legend { display:flex; flex-direction:column; gap:5px; }
.chart-legend-row { display:flex; align-items:center; gap:6px; font-size:12px; color:#374151; }
.chart-legend-dot { width:8px; height:8px; border-radius:2px; flex-shrink:0; }
.chart-legend-name { flex:1; }
.chart-legend-val { font-weight:600; color:#111827; min-width:25px; text-align:left; }
.chart-legend-pct { font-size:10px; color:#9ca3af; min-width:28px; text-align:left; }

/* Properties by region ───────────────────────────────────────────── */
.region-stats-grid { display:grid; grid-template-columns:repeat(auto-fit,minmax(220px,1fr)); gap:10px; }
.region-stat { padding:11px 13px; border:1px solid rgba(62,87,46,.12); border-radius:10px; background:#fbfcf9; }
.region-stat__row { display:flex; align-items:center; justify-content:space-between; gap:12px; }
.region-stat__name { color:#26351f; font-size:13px; font-weight:600; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.region-stat__count { color:#3e572e; font-size:16px; font-weight:700; white-space:nowrap; font-variant-numeric:tabular-nums; }
.region-stat__count small { color:#7b8575; font-size:10px; font-weight:500; }
.region-stat__track { height:5px; margin-top:10px; overflow:hidden; border-radius:5px; background:#e9efe4; }
.region-stat__fill { display:block; height:100%; border-radius:5px; background:linear-gradient(90deg,#a69057,#3e572e); }

/* Tables ──────────────────────────────────────────────────────────── */
.dash-table { width:100%; border-collapse:collapse; font-size:12px; }
.dash-table thead tr { background:#f9fafb; }
.dash-table th { padding:6px 8px; font-weight:600; color:#374151; border-bottom:1px solid #e5e7eb; white-space:nowrap; }
.dash-table td { padding:6px 8px; color:#111827; border-bottom:1px solid #f3f4f6; vertical-align:middle; }
.dash-table tbody tr:hover td { background:#fafafa; }
.id-link { font-weight:600; color:#3e572e; cursor:pointer; }
.id-link:hover { text-decoration:underline; }
.id-link--warn { color:#d97706; }
.ellipsis-cell { max-width:130px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.date-cell { color:#9ca3af; font-size:11px; white-space:nowrap; }
.empty-state { text-align:center; padding:24px 12px; color:#9ca3af; font-size:13px; }
/* Overdue Rentals ─────────────────────────────────────────────────── */
.overdue-row:hover td { background:#fff5f5 !important; }
.overdue-badge { display:inline-block; padding:2px 8px; border-radius:10px; font-size:11px; font-weight:600; }
.overdue-badge--medium   { background:#fef3c7; color:#92400e; }
.overdue-badge--high     { background:#fee2e2; color:#b91c1c; }
.overdue-badge--critical { background:#6f372e; color:#fff3ef; }
.btn-xs { font-size:11px; padding:2px 8px; border-radius:5px; }

/* Badges ──────────────────────────────────────────────────────────── */
.badge { padding:3px 6px; border-radius:5px; font-size:10px; font-weight:600; }
.bg-success   { background:#627d50; color:white; }
.bg-primary   { background:#3e572e; color:white; }
.bg-info      { background:#e1ebda; color:#3e572e; }
.bg-warning   { background:#f5ead0; color:#765d27; }
.bg-danger    { background:#b85c4a; color:white; }
.bg-secondary { background:#7c8278; color:white; }
.bg-light     { background:#eff3eb; color:#455140; }

/* Equal Height Cards ──────────────────────────────────────────────── */
.row.g-2 { display:flex; flex-wrap:wrap; }
.row.g-2 > [class*="col-"] { display:flex; flex-direction:column; }
.kpi-card { flex:1; }

/* Modals ──────────────────────────────────────────────────────────── */
.modal-overlay { position:fixed; top:0; left:0; right:0; bottom:0; background:rgba(0,0,0,0.5); display:flex; align-items:center; justify-content:center; z-index:9999; backdrop-filter:blur(4px); }
.modal-container { background:white; border-radius:14px; max-width:500px; width:90%; max-height:80vh; display:flex; flex-direction:column; box-shadow:0 20px 25px -5px rgba(0,0,0,0.1); animation:modalSlideIn .3s ease; }
@keyframes modalSlideIn { from { transform:translateY(-30px); opacity:0; } to { transform:translateY(0); opacity:1; } }
.modal-header { padding:12px 16px; border-bottom:1px solid #e5e7eb; display:flex; justify-content:space-between; align-items:center; }
.modal-title { font-size:15px; font-weight:600; color:#111827; margin:0; }
.modal-close { background:none; border:none; font-size:22px; cursor:pointer; color:#9ca3af; transition:color .2s; width:26px; height:26px; display:flex; align-items:center; justify-content:center; border-radius:6px; }
.modal-close:hover { color:#a95142; background:#f2ddd7; }
.modal-body { padding:14px; overflow-y:auto; flex:1; }
.modal-footer { padding:10px 14px; border-top:1px solid #e5e7eb; display:flex; justify-content:flex-end; }
.btn-secondary { background:#f3f4f6; color:#374151; border:1px solid #e5e7eb; padding:5px 12px; border-radius:6px; cursor:pointer; font-size:12px; transition:all .2s; }
.btn-secondary:hover { background:#e5e7eb; transform:translateY(-1px); }
.statuses-modal-list, .departments-modal-list { display:flex; flex-direction:column; gap:8px; max-height:400px; overflow-y:auto; }
.status-modal-row, .dept-modal-row { display:flex; align-items:center; justify-content:space-between; padding:6px 8px; background:#f9fafb; border-radius:6px; border:1px solid #e5e7eb; }
.status-modal-info { display:flex; align-items:center; gap:8px; }
.status-modal-name { font-size:12px; font-weight:500; color:#111827; }
.status-modal-count { font-size:13px; font-weight:700; color:#3e572e; }
.dept-modal-info { display:flex; align-items:center; gap:8px; }
.dept-modal-icon { width:26px; height:26px; border-radius:6px; display:flex; align-items:center; justify-content:center; font-size:12px; }
.dept-modal-name { font-size:12px; font-weight:500; color:#111827; }
.dept-modal-stats { display:flex; align-items:center; gap:8px; }
.dept-modal-count { font-size:10px; color:#6b7280; background:#f3f4f6; padding:2px 5px; border-radius:8px; }
.dept-modal-amount { font-size:12px; font-weight:700; color:#111827; white-space:nowrap; }
.order-statuses-modal { display:flex; flex-direction:column; gap:14px; }
.order-status-group h6 { font-size:12px; font-weight:600; color:#374151; margin-bottom:6px; padding-bottom:3px; border-bottom:1px solid #e5e7eb; }
.order-status-item { display:flex; justify-content:space-between; align-items:center; padding:4px 0; }
.order-status-item span:first-child { font-size:11px; color:#4b5563; }
.order-status-item .badge { font-size:9px; padding:2px 5px; }

/* Trend range selector ───────────────────────────────────────────── */
.trend-range-btns {
  display: flex;
  gap: 3px;
  background: #f3f4f6;
  border-radius: 7px;
  padding: 3px;
}
.trend-range-btn {
  border: none;
  background: transparent;
  padding: 3px 10px;
  border-radius: 5px;
  font-size: 11px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  transition: all .18s;
  white-space: nowrap;
}
.trend-range-btn:hover {
  background: #e5e7eb;
  color: #111827;
}
.trend-range-btn--active {
  background: #fff;
  color: #3e572e;
  font-weight: 600;
  box-shadow: 0 1px 4px rgba(0,0,0,.10);
}
.maintenance-response-box{
display:flex;
align-items:center;
gap:16px;
padding:10px;
margin:12px 0;
border-radius:10px;
background:rgba(255,255,255,.12);
border:1px solid rgba(255,255,255,.15);
}

.maintenance-response-icon{
width:30px;
height:25px;
display:flex;
align-items:center;
justify-content:center;
border-radius:50%;
background:rgba(255,255,255,.15);
font-size:15px;
}

.maintenance-response-label{
font-size:10px;
opacity:.85;
}

.maintenance-response-value{
font-size:13px;
font-weight:500;
}

/* ── Dashboard Filter Bar ──────────────────────────────────────────── */
.dash-filter-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 16px;
  flex-wrap: wrap;
  gap: 8px;
}
.dash-filter-bar__left {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}
.dash-filter-bar__title {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}
.dash-filter-bar__right {
  display: flex;
  align-items: center;
  gap: 8px;
}
.dash-filter-active-chips {
  display: flex;
  gap: 5px;
  flex-wrap: wrap;
}
.dash-chip {
  background: #ebf4df;
  color: #3e572e;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 20px;
  border: 1px solid rgba(62,87,46,.16);
}
.dash-filter-btn {
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  border-radius: 7px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  color: #374151;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all .15s;
}
.dash-filter-btn:hover {
  background: #e5e7eb;
}
.dash-filter-btn--clear {
  background: #fef2f2;
  border-color: #fecaca;
  color: #dc2626;
}
.dash-filter-btn--clear:hover {
  background: #fee2e2;
}
.dash-filter-panel {
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 16px;
}
.dash-filter-label {
  font-size: 12px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 4px;
  display: block;
}
/* Filter slide transition */
.filter-slide-enter-active,
.filter-slide-leave-active {
  transition: all .25s ease;
  overflow: hidden;
}
.filter-slide-enter-from,
.filter-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
/* Filter notice banner */
.dash-filter-notice {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  background: #ebf4df;
  border: 1px solid rgba(62,87,46,.18);
  border-radius: 10px;
  padding: 10px 16px;
  font-size: 13px;
  color: #3e572e;
}
.dash-filter-notice strong {
  color: #3e572e;
  font-weight: 600;
}
.dash-filter-notice__clear {
  margin-right: auto;
  background: none;
  border: 1px solid rgba(62,87,46,.32);
  border-radius: 6px;
  padding: 3px 10px;
  font-size: 12px;
  color: #3e572e;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all .15s;
}
.dash-filter-notice__clear:hover {
  background: #dfeccd;
}

.overdue-count-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
}
.overdue-count-badge--ok { background: #d1fae5; color: #065f46; }
.overdue-count-badge--warn { background: #fef3c7; color: #92400e; }
.overdue-count-badge--critical { background: #6f372e; color: #fff3ef; }

/* Brand identity ─────────────────────────────────────────────────── */
.app-content-header {
  padding-top: 1.35rem;
  padding-bottom: 1.15rem;
}
.dashboard-heading {
  position: relative;
  min-height: 132px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  overflow: hidden;
  padding: 24px 30px;
  color: #fff;
  border: 1px solid rgba(166, 144, 87, .42);
  border-radius: 18px;
  background:
    linear-gradient(118deg, transparent 0 68%, rgba(255,255,255,.045) 68% 75%, transparent 75%),
    linear-gradient(135deg, #3e572e 0%, #304624 58%, #1c191a 145%);
  box-shadow: 0 14px 36px rgba(28, 25, 26, .12);
  isolation: isolate;
}
.dashboard-heading::after {
  content: "";
  position: absolute;
  inset: auto -5% -68% 32%;
  height: 135%;
  transform: skewX(-34deg);
  border-top: 1px solid rgba(166,144,87,.28);
  background: rgba(166,144,87,.07);
  z-index: -1;
}
.dashboard-heading__content { position: relative; z-index: 1; }
.dashboard-heading__eyebrow {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 7px;
  color: #d8ca9f;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: .08em;
}
.dashboard-heading__eyebrow::before {
  content: "";
  width: 24px;
  height: 1px;
  background: #a69057;
}
.dashboard-heading h1 {
  margin: 0;
  font-size: clamp(25px, 3vw, 35px);
  font-weight: 650;
  letter-spacing: -.02em;
}
.dashboard-heading p {
  margin: 7px 0 0;
  max-width: 560px;
  color: rgba(255,255,255,.72);
  font-size: 13px;
}
.dashboard-heading__mark {
  position: relative;
  z-index: 1;
  display: grid;
  place-items: center;
  width: 86px;
  height: 86px;
  margin-inline-start: 28px;
  flex: 0 0 auto;
  border: 1px solid rgba(255,255,255,.12);
  border-radius: 50%;
  background: rgba(255,255,255,.94);
  box-shadow: 0 12px 30px rgba(0,0,0,.16);
}
.dashboard-heading__mark img { width: 62px; height: 62px; }

.kpi-card {
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(255,255,255,.12);
  box-shadow: 0 10px 24px rgba(28,25,26,.10);
}
.kpi-card::after {
  content: "";
  position: absolute;
  left: -30px;
  bottom: -58px;
  width: 126px;
  height: 106px;
  transform: skewX(-36deg);
  border: 1px solid rgba(255,255,255,.07);
  background: rgba(255,255,255,.035);
  pointer-events: none;
}
.kpi-card > * { position: relative; z-index: 1; }
.kpi-card--blue   { background: linear-gradient(135deg,#3e572e,#273a20); }
.kpi-card--teal   { background: linear-gradient(135deg,#61794c,#3e572e); }
.kpi-card--indigo { background: linear-gradient(135deg,#1c191a,#3b4531); }
.kpi-card--amber  { background: linear-gradient(135deg,#a69057,#6f6039); }
.kpi-card--violet { background: linear-gradient(135deg,#3e572e,#1c191a); }
.contact-card { background: linear-gradient(135deg,#1c191a,#3e572e); }
.kpi-card__icon,
.compact-stat__icon {
  border: 1px solid rgba(255,255,255,.16);
  background: rgba(255,255,255,.10);
}
.kpi-card__bar { height: 4px; background: rgba(255,255,255,.14); }
.management-progress-fill { background: #d8ca9f; }
.management-stat--yellow .management-stat__icon,
.management-stat--blue .management-stat__icon { color: #d8ca9f; }

.card {
  overflow: hidden;
  border: 1px solid rgba(62,87,46,.11);
  border-radius: 14px;
  box-shadow: 0 8px 24px rgba(28,25,26,.055);
}
.card-header {
  min-height: 48px;
  border-bottom: 1px solid rgba(62,87,46,.09);
  background: linear-gradient(90deg, rgba(235,244,223,.72), rgba(255,255,255,.95) 45%);
}
.card-title { color: #26351f; font-weight: 600; }
.card-title i { color: #a69057 !important; }

.dash-filter-bar {
  padding: 12px 16px;
  border-color: rgba(62,87,46,.16);
  border-radius: 12px;
  box-shadow: 0 7px 20px rgba(28,25,26,.04);
}
.dash-filter-bar__title { color: #26351f; }
.dash-filter-bar__left > i { color: #a69057 !important; }
.dash-chip {
  border-color: rgba(62,87,46,.16);
  color: #3e572e;
  background: #ebf4df;
}
.dash-filter-btn {
  border-color: rgba(62,87,46,.14);
  color: #3e572e;
  background: #f4f7f0;
}
.dash-filter-btn:hover { border-color: #a69057; color: #1c191a; background: #fff; }
.dash-filter-panel {
  border-color: rgba(62,87,46,.16);
  background: linear-gradient(135deg,#f5f8f1,#ebf4df);
}
.dash-filter-notice {
  border-color: rgba(62,87,46,.18);
  color: #3e572e;
  background: #ebf4df;
}
.dash-filter-notice strong,
.dash-filter-notice__clear { color: #3e572e; }
.dash-filter-notice__clear { border-color: rgba(62,87,46,.32); }
.dash-filter-notice__clear:hover { background: #dfeccd; }

.dash-table thead tr { background: #f2f6ed; }
.dash-table th { color: #3e572e; border-bottom-color: rgba(62,87,46,.13); }
.dash-table td { color: #1c191a; border-bottom-color: rgba(62,87,46,.07); }
.dash-table tbody tr:hover td { background: #f8faf5; }
.id-link { color: #3e572e; }
.chart-legend-val,
.donut-center__val { color: #1c191a; }
.chart-legend-row { color: #4d5548; }
.chart-legend-dot { border-radius: 50%; }

.mini-drawer-tab--open,
.trend-range-btn--active {
  border-color: rgba(62,87,46,.25);
  color: #3e572e;
  background: #ebf4df;
}
.mini-drawer-tab i,
.trend-range-btn--active { color: #3e572e; }
.mini-drawer__item:hover { border-color: rgba(166,144,87,.35); }
.modal-container { border: 1px solid rgba(166,144,87,.25); }
.status-modal-count { color: #3e572e; }
.text-primary { color: #3e572e !important; }
.spinner-border.text-primary { color: #a69057 !important; }

/* Elegant data palette ───────────────────────────────────────────── */
.data-dot--positive { background: #7f9a6a; }
.data-dot--pending  { background: #d8b365; }
.data-dot--danger   { background: #c77462; }
.data-dot--neutral  { background: #8c9387; }
.data-dot--muted    { background: #c3c8bd; }

.kpi-card__value,
.compact-stat__value,
.management-card__total,
.management-stat__val { color: #fffdf8; font-variant-numeric: tabular-nums; }
.kpi-card__month,
.compact-dept-val,
.management-progress-pct { color: #eadcb2; }
.vertical-pill__value,
.order-item__value {
  border: 1px solid rgba(255,255,255,.08);
  background: rgba(28,25,26,.20);
  color: #fffdf8;
  font-variant-numeric: tabular-nums;
}
.order-item__value--warning { background: rgba(166,144,87,.28); color: #f1e2b6; }
.order-item__value--danger  { background: rgba(184,92,74,.27); color: #ffd7cf; }
.order-item__value--success { background: rgba(127,154,106,.28); color: #e2f0d8; }
.order-item__value--info    { background: rgba(143,162,124,.25); color: #edf4e7; }
.management-stat--yellow .management-stat__icon { color: #e3c77e; }
.management-stat--blue .management-stat__icon   { color: #bdcdaf; }
.management-stat--green .management-stat__icon  { color: #cfe2c0; }
.management-stat--red .management-stat__icon    { color: #edb0a2; }

.mini-drawer {
  border-right-color: rgba(62,87,46,.14);
  background: #fbfcf9;
}
.mini-drawer__header { border-bottom-color: rgba(62,87,46,.09); background: #f2f6ed; }
.mini-drawer__title,
.mini-drawer__val { color: #26351f; }
.mini-drawer__lbl { color: #737b6f; }
.mini-drawer__item { border-color: rgba(62,87,46,.08); background: #fff; }
.mini-drawer__item:hover { background: #f7faf4; }
.mini-drawer__arrow { color: #b7bdB2; }
.mini-drawer__icon--green { color: #3e572e; background: rgba(62,87,46,.13); }
.mini-drawer__icon--olive { color: #61794c; background: rgba(97,121,76,.13); }
.mini-drawer__icon--sage { color: #7b9069; background: rgba(123,144,105,.14); }
.mini-drawer__icon--gold { color: #9b7e3d; background: rgba(166,144,87,.16); }
.mini-drawer__icon--danger { color: #a95142; background: rgba(184,92,74,.13); }
.mini-drawer__icon--ink { color: #4f494a; background: rgba(28,25,26,.09); }

.chart-legend-val { color: #3e572e; font-variant-numeric: tabular-nums; }
.chart-legend-pct { color: #927a43; }
.donut-center__val { color: #26351f; font-variant-numeric: tabular-nums; }
.donut-center__lbl,
.date-cell,
.empty-state { color: #858d81; }

.badge {
  padding: 4px 8px;
  border: 1px solid transparent;
  border-radius: 20px;
  letter-spacing: 0;
}
.bg-success { border-color: #627d50 !important; background: #627d50 !important; color: #fff !important; }
.bg-primary { border-color: #3e572e !important; background: #3e572e !important; color: #fff !important; }
.bg-info { border-color: #c6d6bc !important; background: #e1ebda !important; color: #3e572e !important; }
.bg-warning { border-color: #e0c888 !important; background: #f5ead0 !important; color: #765d27 !important; }
.bg-danger { border-color: #b85c4a !important; background: #b85c4a !important; color: #fff !important; }
.bg-secondary { border-color: #7c8278 !important; background: #7c8278 !important; color: #fff !important; }
.bg-light { border-color: #dde4d8 !important; background: #eff3eb !important; color: #455140 !important; }

.data-link--positive { color: #4f6a3e !important; }
.data-link--gold { color: #8c7136 !important; }
.data-link--danger,
.data-value--danger { color: #a95142 !important; }
.data-icon--gold { color: #a69057 !important; }
.id-link--warn { color: #a96e28; }

.overdue-row:hover td { background: #fff9f6 !important; }
.overdue-badge--medium { border: 1px solid #e5cf95; background: #f5ead0; color: #765d27; }
.overdue-badge--high { border: 1px solid #dfb3a8; background: #f2ddd7; color: #934636; }
.overdue-badge--critical { border: 1px solid #6f372e; background: #6f372e; color: #fff3ef; }
.overdue-count-badge--ok { background: #e1ebda; color: #3e572e; }
.overdue-count-badge--warn { background: #f5ead0; color: #765d27; }
.overdue-count-badge--critical { background: #6f372e; color: #fff3ef; }

.status-modal-row,
.dept-modal-row { border-color: rgba(62,87,46,.09); background: #f7faf4; }
.status-modal-count,
.dept-modal-amount { color: #3e572e; font-variant-numeric: tabular-nums; }
.dept-modal-count { color: #7c745e; background: #eee9dc; }
.order-status-group h6 { color: #3e572e; border-bottom-color: rgba(62,87,46,.12); }

@media (max-width: 767.98px) {
  .dashboard-heading { min-height: 118px; padding: 20px; }
  .dashboard-heading p { max-width: 78%; font-size: 11px; }
  .dashboard-heading__mark { width: 64px; height: 64px; margin-inline-start: 12px; }
  .dashboard-heading__mark img { width: 46px; height: 46px; }
}

</style>
